import { memo, useState, useCallback } from 'react';
import styled from '@emotion/styled';
import { DEFAULT_SEGMENT_INITIAL_MESSAGE, DEFAULT_SEGMENT_QUESTIONS } from '@trinity/chat';
import { ChatConfigForm } from './ChatConfigForm';
import { ChatConfigReadOnly } from './ChatConfigReadOnly';

type SegmentChatConfigProps = {
  initialMessage: string | null | undefined;
  questions: string[] | null | undefined;
  onSave: (data: { initialMessage: string | null; questions: string[] | null }) => Promise<void>;
  isSaving?: boolean;
  whitelabelEnabled?: boolean;
  primaryColor?: string;
};

const SegmentInitialChatConfig = ({
  initialMessage,
  questions,
  onSave,
  isSaving = false,
  whitelabelEnabled,
  primaryColor,
}: SegmentChatConfigProps) => {
  const [isEditing, setIsEditing] = useState(false);

  const displayMessage = initialMessage ?? DEFAULT_SEGMENT_INITIAL_MESSAGE;
  // null = never configured → show defaults; [] = explicitly cleared → show nothing
  const displayQuestions = questions === null || questions === undefined ? [...DEFAULT_SEGMENT_QUESTIONS] : questions;

  const handleEdit = useCallback(() => {
    setIsEditing(true);
  }, []);

  const handleCancel = useCallback(() => {
    setIsEditing(false);
  }, []);

  const handleSave = useCallback(async (data: { initialMessage: string | null; questions: string[] }) => {
    await onSave({ initialMessage: data.initialMessage, questions: data.questions });
    setIsEditing(false);
  }, [onSave]);

  return (
    <ChatConfigSection>
      {isEditing ? (
        <ChatConfigForm
          initialMessage={displayMessage}
          initialQuestions={displayQuestions}
          onSave={handleSave}
          onCancel={handleCancel}
          isSaving={isSaving}
        />
      ) : (
        <ChatConfigReadOnly
          initialMessage={displayMessage}
          questions={displayQuestions}
          onEdit={handleEdit}
          whitelabelEnabled={whitelabelEnabled}
          primaryColor={primaryColor}
        />
      )}
    </ChatConfigSection>
  );
};

export const SegmentChatConfig = memo(SegmentInitialChatConfig);

const ChatConfigSection = styled.div`
  margin-top: 32px;
  padding-top: 24px;
  border-top: 1px solid var(--gray-l);
`;
